import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFormRegistryAggregates } from '../FormRegistryContext';

interface FormRegistryDevToolsProps {
	position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
}

let devToolsCount = 0;

export function FormRegistryDevTools({ position = 'bottom-right' }: FormRegistryDevToolsProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [expandedForms, setExpandedForms] = useState<Set<string>>(() => new Set());
	// eslint-disable-next-line react/hook-use-state
	const [instanceIndex] = useState(() => devToolsCount++);
	const forms = useFormRegistryAggregates();
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		ref.current?.removeAttribute('inert');
	}, []);

	const toggle = useCallback(() => setIsOpen(prev => !prev), []);

	const toggleForm = useCallback((id: string) => {
		setExpandedForms(prev => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	if (process.env.NODE_ENV === 'production') {
		return null;
	}

	const offset = instanceIndex * 40;
	const bottom = instanceIndex === 1 ? 30 : offset;
	const offsetStyle = position.includes('bottom') ? { bottom: `${bottom}px` } : { top: `${16 + offset}px` };

	const entries = Array.from(forms.entries());

	return createPortal(
		<div
			ref={ref}
			data-react-aria-top-layer
			className="fixed z-[9999]"
			style={{
				...offsetStyle,
				[position.includes('left') ? 'left' : 'right']: '16px'
			}}
		>
			<button
				onClick={toggle}
				type="button"
				className="relative flex size-12 items-center justify-center rounded-full bg-purple-600 text-white shadow-lg transition-colors hover:bg-purple-700"
				title="Form Registry DevTools"
			>
				📋
				{entries.length > 0 && (
					<span className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold">
						{entries.length}
					</span>
				)}
			</button>

			{/* Drawer */}
			{isOpen && (
				<div className="absolute bottom-16 right-0 h-[600px] w-[500px] overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xl">
					<div className="flex items-center justify-between border-b bg-gray-100 px-4 py-2">
						<h3 className="font-semibold text-gray-800">Form Registry DevTools ({entries.length} forms)</h3>
						<button type="button" onClick={toggle} className="text-gray-500 hover:text-gray-700">
							✕
						</button>
					</div>

					<div className="h-[calc(100%-40px)] overflow-auto p-4 text-sm">
						{entries.length === 0 ? (
							<div className="italic text-gray-500">No forms registered</div>
						) : (
							<div className="space-y-3">
								{entries.map(([id, f]) => {
									const isExpanded = expandedForms.has(id);
									const errorCount = f.errors ? Object.keys(f.errors).length : 0;
									const touchedCount = f.touched ? Object.keys(f.touched).length : 0;
									return (
										<div key={id} className="overflow-hidden rounded-lg border border-gray-200">
											<button
												type="button"
												onClick={() => toggleForm(id)}
												className="flex w-full items-center justify-between bg-gray-50 px-3 py-2 text-left hover:bg-gray-100"
											>
												<div className="flex items-center gap-2">
													<span className="font-mono text-xs text-gray-500">{isExpanded ? '▼' : '▶'}</span>
													<span className="font-semibold text-gray-800">{id}</span>
												</div>
												<div className="flex items-center gap-2 text-xs">
													{errorCount > 0 && (
														<span className="rounded bg-red-100 px-2 py-0.5 text-red-700">{errorCount} errors</span>
													)}
													{f.hasUnsavedChanges && (
														<span className="rounded bg-yellow-100 px-2 py-0.5 text-yellow-700">dirty</span>
													)}
													{f.isSubmitting && (
														<span className="rounded bg-blue-100 px-2 py-0.5 text-blue-700">submitting</span>
													)}
													{f.readOnly && <span className="rounded bg-gray-200 px-2 py-0.5 text-gray-700">read-only</span>}
												</div>
											</button>

											{isExpanded && (
												<div className="space-y-3 border-t border-gray-200 bg-white p-3">
													{/* Status */}
													<div>
														<h4 className="mb-1 text-xs font-medium text-gray-700">Status</h4>
														<div className="grid grid-cols-2 gap-x-2 gap-y-0.5 rounded bg-gray-50 p-2 text-xs">
															<div>
																formStatus: <span className="font-mono">{f.formStatus}</span>
															</div>
															<div>
																isValid: <span className="font-mono">{f.isValid.toString()}</span>
															</div>
															<div>
																isSubmitting: <span className="font-mono">{f.isSubmitting.toString()}</span>
															</div>
															<div>
																isValidating: <span className="font-mono">{f.isValidating.toString()}</span>
															</div>
															<div>
																dirty: <span className="font-mono">{f.dirty.toString()}</span>
															</div>
															<div>
																submitCount: <span className="font-mono">{f.submitCount}</span>
															</div>
															<div>
																hasErrors: <span className="font-mono">{f.hasErrors.toString()}</span>
															</div>
															<div>
																errorCount: <span className="font-mono">{f.errorCount}</span>
															</div>
															<div>
																hasUnsavedChanges: <span className="font-mono">{f.hasUnsavedChanges.toString()}</span>
															</div>
															<div>
																canBeSubmitted: <span className="font-mono">{f.canBeSubmitted.toString()}</span>
															</div>
															<div>
																canBeReset: <span className="font-mono">{f.canBeReset.toString()}</span>
															</div>
															<div>
																readOnly: <span className="font-mono">{f.readOnly.toString()}</span>
															</div>
														</div>
													</div>

													{/* Values */}
													<div>
														<h4 className="mb-1 text-xs font-medium text-gray-700">Values</h4>
														<pre className="max-h-40 overflow-auto rounded bg-gray-50 p-2 text-xs">
															{JSON.stringify(f.values, null, 2)}
														</pre>
													</div>

													{/* Errors */}
													{errorCount > 0 && (
														<div>
															<h4 className="mb-1 text-xs font-medium text-red-600">Errors ({errorCount})</h4>
															<pre className="max-h-40 overflow-auto rounded bg-red-50 p-2 text-xs">
																{JSON.stringify(f.errors, null, 2)}
															</pre>
														</div>
													)}

													{/* Touched */}
													{touchedCount > 0 && (
														<div>
															<h4 className="mb-1 text-xs font-medium text-yellow-600">Touched ({touchedCount})</h4>
															<pre className="max-h-32 overflow-auto rounded bg-yellow-50 p-2 text-xs">
																{JSON.stringify(f.touched, null, 2)}
															</pre>
														</div>
													)}

													{/* Required Fields */}
													{f.requiredFields && f.requiredFields.length > 0 && (
														<div>
															<h4 className="mb-1 text-xs font-medium text-gray-700">
																Required Fields ({f.requiredFields.length})
															</h4>
															<pre className="max-h-32 overflow-auto rounded bg-gray-50 p-2 text-xs">
																{JSON.stringify(f.requiredFields, null, 2)}
															</pre>
														</div>
													)}

													{/* Field Labels */}
													{f.fieldLabels && f.fieldLabels.size > 0 && (
														<div>
															<h4 className="mb-1 text-xs font-medium text-gray-700">
																Field Labels ({f.fieldLabels.size})
															</h4>
															<pre className="max-h-32 overflow-auto rounded bg-gray-50 p-2 text-xs">
																{JSON.stringify(Object.fromEntries(f.fieldLabels), null, 2)}
															</pre>
														</div>
													)}
												</div>
											)}
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			)}
		</div>,
		document.body
	);
}
